import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { Category } from '../types/foundObject';
import {
  attributeSelectOptions,
  getFilterableAttributeEntries,
  pruneDependentAttributeFilters,
  resolveConditionalDependsOnValue,
  resolveConditionalSelectLabel,
} from '../utils/categoryAttributeFilters';
import type { CategoryAttribute } from '../types/foundObject';

type Props = {
  category: Category | undefined;
  attributeFilters: Record<string, string>;
  onAttributeFiltersChange: (next: Record<string, string>) => void;
  compact?: boolean;
};

function renderControl(
  key: string,
  def: CategoryAttribute,
  value: string,
  attributeFilters: Record<string, string>,
  onChange: (key: string, v: string) => void
) {
  const label =
    def.type === 'conditional_select'
      ? resolveConditionalSelectLabel(def, resolveConditionalDependsOnValue(key, def, attributeFilters))
      : def.label || key;

  if (def.type === 'boolean') {
    return (
      <FormControl size="small" fullWidth key={key}>
        <InputLabel id={`attr-filt-${key}`}>{label}</InputLabel>
        <Select
          labelId={`attr-filt-${key}`}
          label={label}
          value={value || '__all__'}
          onChange={(e) => onChange(key, e.target.value === '__all__' ? '' : String(e.target.value))}
        >
          <MenuItem value="__all__">Tous</MenuItem>
          <MenuItem value="true">Oui</MenuItem>
          <MenuItem value="false">Non</MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (def.type === 'select' || def.type === 'color_select' || def.type === 'conditional_select') {
    const isConditional = def.type === 'conditional_select';
    const opts = attributeSelectOptions(key, def as Parameters<typeof attributeSelectOptions>[1], attributeFilters);
    const disabled = isConditional && opts.length === 0;
    const selectValue = value && opts.includes(value) ? value : '__all__';

    return (
      <FormControl size="small" fullWidth key={key} disabled={disabled}>
        <InputLabel id={`attr-filt-${key}`}>{label}</InputLabel>
        <Select
          labelId={`attr-filt-${key}`}
          label={label}
          value={disabled ? '__all__' : selectValue}
          onChange={(e) => onChange(key, e.target.value === '__all__' ? '' : String(e.target.value))}
        >
          <MenuItem value="__all__">{disabled ? 'Choisir le champ parent d’abord' : 'Tous'}</MenuItem>
          {opts.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      key={key}
      size="small"
      fullWidth
      label={label}
      placeholder={def.placeholder || ''}
      value={value}
      onChange={(e) => onChange(key, e.target.value)}
    />
  );
}

const CategoryAttributeFilterControls: React.FC<Props> = ({
  category,
  attributeFilters,
  onAttributeFiltersChange,
  compact = false,
}) => {
  const entries = getFilterableAttributeEntries(category);

  if (!category || entries.length === 0) {
    return null;
  }

  const handleChange = (key: string, v: string) => {
    const next = { ...attributeFilters };
    if (v) {
      next[key] = v;
    } else {
      delete next[key];
    }
    onAttributeFiltersChange(pruneDependentAttributeFilters(next, category));
  };

  return (
    <Box sx={{ mt: compact ? 1.5 : 2 }}>
      {!compact ? (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Filtres « {category.name} »
        </Typography>
      ) : null}
      <Stack spacing={1.5}>
        {entries.map(([key, def]) =>
          renderControl(key, def, attributeFilters[key] ?? '', attributeFilters, handleChange)
        )}
      </Stack>
    </Box>
  );
};

export default CategoryAttributeFilterControls;
